import { FC, useEffect } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	MenuItem,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	createPgLegalEntityRequest,
	resetPgSaveSuccess,
	updatePgLegalEntityRequest,
} from 'redux/reducers/pgSettings/reducer';
import { selectPgSaveLoading, selectPgSaveSuccess } from 'redux/reducers/pgSettings/selectors';
import { TPgAirwallexAccount, TPgLegalEntity } from 'services/api/paymentGateway/types';
import { PG_CODE_REGEX } from '../../constants';

type TLegalEntityDialogProps = {
	open: boolean;
	onClose: () => void;
	legalEntityToEdit: TPgLegalEntity | null;
	accounts: TPgAirwallexAccount[];
};

type TLegalEntityFormValues = {
	code: string;
	name: string;
	legalName: string;
	countryCode: string;
	registrationNumber: string;
	vatNumber: string;
	address: string;
	supportEmail: string;
	airwallexAccountId: number | '';
	isDefault: boolean;
	isActive: boolean;
};

const validationSchema = Yup.object({
	code: Yup.string()
		.required('Code is required')
		.matches(PG_CODE_REGEX, 'Lowercase letters, digits and "_", 2–40 characters'),
	name: Yup.string().trim().required('Short name is required').max(300, 'Max 300 characters'),
	legalName: Yup.string().trim().required('Legal name is required').max(300, 'Max 300 characters'),
	countryCode: Yup.string()
		.trim()
		.required('Country is required')
		.matches(/^[A-Za-z]{2}$/, 'ISO 3166-1 alpha-2 code, e.g. EE'),
	registrationNumber: Yup.string().max(100, 'Max 100 characters'),
	vatNumber: Yup.string().max(100, 'Max 100 characters'),
	address: Yup.string().max(1000, 'Max 1000 characters'),
	supportEmail: Yup.string().trim().email('Not a valid e-mail').max(300, 'Max 300 characters'),
	airwallexAccountId: Yup.number()
		.typeError('Airwallex account is required')
		.required('Airwallex account is required'),
});

const optional = (value: string): string | null => value.trim() || null;

/** Create / edit a legal entity (03 §4.4). `code` is set once. */
export const LegalEntityDialog: FC<TLegalEntityDialogProps> = ({
	open,
	onClose,
	legalEntityToEdit,
	accounts,
}) => {
	const dispatch = useDispatch();
	const isEdit = !!legalEntityToEdit;
	const loading = useSelector(selectPgSaveLoading('legalEntity'));
	const success = useSelector(selectPgSaveSuccess('legalEntity'));

	const defaultAccount = accounts.find((a) => a.isDefault) ?? accounts[0];

	const formik = useFormik<TLegalEntityFormValues>({
		initialValues: {
			code: legalEntityToEdit?.code ?? '',
			name: legalEntityToEdit?.name ?? '',
			legalName: legalEntityToEdit?.legalName ?? '',
			countryCode: legalEntityToEdit?.countryCode ?? '',
			registrationNumber: legalEntityToEdit?.registrationNumber ?? '',
			vatNumber: legalEntityToEdit?.vatNumber ?? '',
			address: legalEntityToEdit?.address ?? '',
			supportEmail: legalEntityToEdit?.supportEmail ?? '',
			airwallexAccountId: legalEntityToEdit?.airwallexAccountId ?? defaultAccount?.id ?? '',
			isDefault: legalEntityToEdit?.isDefault ?? false,
			isActive: legalEntityToEdit?.isActive ?? true,
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const body = {
				name: values.name.trim(),
				legalName: values.legalName.trim(),
				countryCode: values.countryCode.trim().toUpperCase(),
				registrationNumber: optional(values.registrationNumber),
				vatNumber: optional(values.vatNumber),
				address: optional(values.address),
				supportEmail: optional(values.supportEmail),
				airwallexAccountId: Number(values.airwallexAccountId),
				isDefault: values.isDefault,
				isActive: values.isActive,
			};
			if (isEdit && legalEntityToEdit) {
				dispatch(updatePgLegalEntityRequest({ id: legalEntityToEdit.id, body }));
			} else {
				dispatch(createPgLegalEntityRequest({ ...body, code: values.code.trim() }));
			}
		},
	});

	useEffect(() => {
		if (!success) return;
		dispatch(resetPgSaveSuccess('legalEntity'));
		formik.resetForm();
		onClose();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [success]);

	const handleClose = () => {
		if (loading) return;
		formik.resetForm();
		onClose();
	};

	const textField = (
		name: keyof TLegalEntityFormValues,
		label: string,
		extra?: { helperText?: string; multiline?: boolean; disabled?: boolean },
	) => (
		<TextField
			label={label}
			name={name}
			value={formik.values[name]}
			onChange={formik.handleChange}
			onBlur={formik.handleBlur}
			error={formik.touched[name] && !!formik.errors[name]}
			helperText={(formik.touched[name] && formik.errors[name]) || extra?.helperText}
			multiline={extra?.multiline}
			minRows={extra?.multiline ? 2 : undefined}
			disabled={extra?.disabled}
			size="small"
		/>
	);

	// Unsetting "default" is done by making another legal entity default.
	const defaultLocked = !!legalEntityToEdit?.isDefault;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle color="primary">
				{isEdit ? `Edit legal entity ${legalEntityToEdit?.code}` : 'Add legal entity'}
			</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					{textField('code', 'Code', {
						disabled: isEdit,
						helperText: isEdit
							? 'The code cannot be changed'
							: 'e.g. eas_project; cannot be changed later',
					})}
					{textField('name', 'Short name', { helperText: 'Shown in receipts and e-mails' })}
					{textField('legalName', 'Legal name', { helperText: 'Shown on the payment page' })}
					{textField('countryCode', 'Country code', { helperText: 'ISO 3166-1 alpha-2, e.g. EE' })}
					{textField('registrationNumber', 'Registration number')}
					{textField('vatNumber', 'VAT number')}
					{textField('address', 'Address', { multiline: true })}
					{textField('supportEmail', 'Support e-mail')}
					<TextField
						select
						label="Airwallex account"
						name="airwallexAccountId"
						value={formik.values.airwallexAccountId}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.airwallexAccountId && !!formik.errors.airwallexAccountId}
						helperText={
							(formik.touched.airwallexAccountId && formik.errors.airwallexAccountId) ||
							'An active legal entity needs an active account'
						}
						size="small"
					>
						{accounts.map((account) => (
							<MenuItem key={account.id} value={account.id}>
								{account.displayName} ({account.code}, {account.environment})
								{account.isActive ? '' : ' — inactive'}
							</MenuItem>
						))}
					</TextField>
					<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
						<FormControlLabel
							control={
								<Switch
									checked={formik.values.isActive}
									onChange={(e) => {
										void formik.setFieldValue('isActive', e.target.checked);
									}}
								/>
							}
							label="Active"
						/>
						<FormControlLabel
							control={
								<Switch
									checked={formik.values.isDefault}
									disabled={defaultLocked}
									onChange={(e) => {
										void formik.setFieldValue('isDefault', e.target.checked);
									}}
								/>
							}
							label="Default"
						/>
					</Box>
					{defaultLocked && (
						<Typography variant="caption" color="text.secondary">
							To change the default legal entity, make another one default.
						</Typography>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
